// =============================================================================
// cardForge.ts — Renderizador PROCEDURAL do card de upgrade (moldura dourada,
// gemas facetadas, banner de título, barra de mecânica e painel de descrição).
// Portado de public/gerador-de-cards.html ("Forja de Cards") pra rodar em jogo,
// desenhando tudo em canvas por CÓDIGO — nenhuma moldura precisa virar PNG.
//
// A ÚNICA coisa que você troca por carta é a ARTE (imagem de IA), o TÍTULO, a
// DESCRIÇÃO, a TAG de mecânica e as CORES das gemas/banner. Isso tudo vem da
// config central em cardArt.ts — este arquivo só sabe DESENHAR.
//
// Resolução lógica 300x468 ampliada com nearest-neighbor. Margem de segurança
// pras gemas de canto e a crista do topo nunca serem cortadas.
// =============================================================================

export interface ForgeCardConfig {
  title: string;          // banner dourado no topo
  tag: string;            // barra de mecânica (ex.: "cone", "bounce", "passiva")
  desc: string;           // painel de descrição (auto-encaixa e trunca)
  theme: string;          // cor do banner do título (hex)
  gemTop: string;         // gema da crista (topo)
  gemCorner: string;      // gemas dos 4 cantos
  gemL: string;           // gema esquerda da barra de mecânica
  gemR: string;           // gema direita da barra de mecânica
  img?: HTMLImageElement | null;   // arte já carregada (ou null = placeholder)
  imgZoom?: number;       // zoom da arte (1 = cover). Opcional, default 1.
  imgOffX?: number;       // deslocamento X da arte em px lógicos. Default 0.
  imgOffY?: number;       // deslocamento Y da arte em px lógicos. Default 0.
}

const LW = 300, LH = 468;

// Canvas lógico único reaproveitado a cada desenho (evita alocar por card)
let logical: HTMLCanvasElement | null = null;
let lctx: CanvasRenderingContext2D | null = null;
function ensureLogical(): CanvasRenderingContext2D {
  if (!lctx) {
    logical = document.createElement('canvas');
    logical.width = LW; logical.height = LH;
    lctx = logical.getContext('2d')!;
  }
  return lctx;
}

// Variante DEITADA (mobile em pé): arte à esquerda, banner/mecânica/descrição à
// direita. Empilhar 3 cartas deitadas cabe bem numa tela vertical.
const LWH = 470, LHH = 232;
let logicalH: HTMLCanvasElement | null = null;
let lctxH: CanvasRenderingContext2D | null = null;
function ensureLogicalH(): CanvasRenderingContext2D {
  if (!lctxH) {
    logicalH = document.createElement('canvas');
    logicalH.width = LWH; logicalH.height = LHH;
    lctxH = logicalH.getContext('2d')!;
  }
  return lctxH;
}

// ---------- cores ----------
function hexToRgb(h: string): [number, number, number] {
  h = h.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b]
    .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0'))
    .join('');
}
function shade(hex: string, f: number): string {
  const [r, g, b] = hexToRgb(hex);
  return f >= 0
    ? rgbToHex(r + (255 - r) * f, g + (255 - g) * f, b + (255 - b) * f)
    : rgbToHex(r * (1 + f), g * (1 + f), b * (1 + f));
}

const GOLD = '#c79a30', GOLD_HI = '#f6d96a', GOLD_MID = '#e0b448', GOLD_LO = '#7c5716', GOLD_DK = '#4c340c';
const OUT = '#160f05', DARK = '#14110c', PANEL = '#1a1610';

// ---------- primitivas ----------
function px(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, c: string): void {
  ctx.fillStyle = c;
  ctx.fillRect(x | 0, y | 0, w | 0, h | 0);
}
function bevelRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, base: string, t = 2): void {
  px(ctx, x, y, w, h, base);
  px(ctx, x, y, w, t, shade(base, .35));
  px(ctx, x, y, t, h, shade(base, .18));
  px(ctx, x, y + h - t, w, t, shade(base, -.45));
  px(ctx, x + w - t, y, t, h, shade(base, -.3));
}
function diamondFill(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, color: string): void {
  for (let i = 0; i < r; i++) {
    const w = (r - i) * 2;
    px(ctx, cx - w / 2, cy - i - 1, w, 1, color);
    px(ctx, cx - w / 2, cy + i, w, 1, color);
  }
}
function tri(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, x3: number, y3: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.lineTo(x3, y3);
  ctx.closePath(); ctx.fill();
}
function sparkle(ctx: CanvasRenderingContext2D, x: number, y: number, a = .9): void {
  ctx.fillStyle = `rgba(255,255,255,${a * .55})`;
  ctx.fillRect(x, y - 3, 1, 7); ctx.fillRect(x - 3, y, 7, 1);
  ctx.fillStyle = `rgba(255,255,255,${a})`;
  ctx.fillRect(x - 1, y - 1, 3, 3);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(x, y, 1, 1);
}

// Gema facetada v2 — coroa + cinta + mesa, engaste dourado com garras
function gem(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, color: string, mount = true): void {
  if (mount) {
    diamondFill(ctx, cx, cy + 1, r + 6, OUT);
    diamondFill(ctx, cx, cy, r + 6, GOLD_DK);
    diamondFill(ctx, cx, cy, r + 5, GOLD);
    tri(ctx, cx, cy - r - 5, cx - r - 5, cy, cx, cy, shade(GOLD, .32));
    tri(ctx, cx, cy + r + 5, cx + r + 5, cy, cx, cy, shade(GOLD, -.28));
    diamondFill(ctx, cx, cy, r + 2, GOLD_LO);
    ([[0, -r - 6], [r + 6, 0], [0, r + 6], [-r - 6, 0]] as const).forEach(([dx, dy]) => {
      px(ctx, cx + dx - 1, cy + dy - 1, 3, 3, GOLD_LO);
      px(ctx, cx + dx - 1, cy + dy - 1, 2, 2, GOLD_HI);
    });
  }
  diamondFill(ctx, cx, cy, r + 1, OUT);
  tri(ctx, cx, cy - r, cx + r, cy, cx, cy, shade(color, .08));
  tri(ctx, cx, cy - r, cx - r, cy, cx, cy, shade(color, .42));
  tri(ctx, cx, cy + r, cx - r, cy, cx, cy, shade(color, -.15));
  tri(ctx, cx, cy + r, cx + r, cy, cx, cy, shade(color, -.5));
  const g = Math.max(3, (r * .62) | 0);
  tri(ctx, cx, cy - g, cx + g, cy, cx, cy, shade(color, .22));
  tri(ctx, cx, cy - g, cx - g, cy, cx, cy, shade(color, .6));
  tri(ctx, cx, cy + g, cx - g, cy, cx, cy, shade(color, 0));
  tri(ctx, cx, cy + g, cx + g, cy, cx, cy, shade(color, -.32));
  diamondFill(ctx, cx, cy, Math.max(2, (r * .34) | 0), shade(color, .75));
  ctx.strokeStyle = 'rgba(0,0,0,.35)'; ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx, cy - r); ctx.lineTo(cx, cy + r);
  ctx.moveTo(cx - r, cy); ctx.lineTo(cx + r, cy);
  ctx.moveTo(cx - g, cy); ctx.lineTo(cx, cy - g); ctx.lineTo(cx + g, cy); ctx.lineTo(cx, cy + g); ctx.closePath();
  ctx.stroke();
  px(ctx, cx + ((r * .3) | 0), cy + ((r * .55) | 0), 1, 1, 'rgba(255,255,255,.5)');
  px(ctx, cx - ((r * .45) | 0), cy - ((r * .5) | 0), 2, 2, '#ffffff');
  px(ctx, cx - ((r * .15) | 0), cy - ((r * .72) | 0), 1, 1, 'rgba(255,255,255,.85)');
}

// rebite dourado
function stud(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  px(ctx, x - 1, y - 1, 3, 3, GOLD_LO);
  px(ctx, x - 1, y - 1, 2, 2, GOLD_HI);
}

// Encaixa a descrição no painel: escolhe o maior corpo de fonte VT323 que caiba
// em `maxW` x `maxH` quebrando em linhas; trunca a última linha com "…" se o
// texto for grande demais (o card espera descrições curtas — ver cardArt.ts).
interface DescLayout { font: string; lines: string[]; lineHeight: number; }
function layoutDesc(ctx: CanvasRenderingContext2D, text: string, maxW: number, maxH: number): DescLayout {
  const clean = text.replace(/\s+/g, ' ').trim();
  const FLOOR = 15;   // piso de corpo: descrições curtas (ver cardArt.ts) ficam grandes
  for (let fs = 24; fs >= FLOOR; fs--) {
    const lh = Math.round(fs * 0.92);
    const maxLines = Math.max(1, Math.floor(maxH / lh));
    const font = `${fs}px VT323, monospace`;
    ctx.font = font;
    const words = clean.split(' ');
    const lines: string[] = [];
    let cur = '';
    for (const w of words) {
      const t = cur ? cur + ' ' + w : w;
      if (ctx.measureText(t).width <= maxW) cur = t;
      else { if (cur) lines.push(cur); cur = w; }
    }
    if (cur) lines.push(cur);
    if (lines.length <= maxLines) return { font, lines, lineHeight: lh };
    // Não coube nem no menor corpo: trunca com reticências na última linha visível
    if (fs === FLOOR) {
      const kept = lines.slice(0, maxLines);
      let last = kept[maxLines - 1] ?? '';
      while (last && ctx.measureText(last + '…').width > maxW) last = last.slice(0, -1).trimEnd();
      kept[maxLines - 1] = last + '…';
      return { font, lines: kept, lineHeight: lh };
    }
  }
  return { font: `${FLOOR}px VT323, monospace`, lines: [clean], lineHeight: FLOOR };
}

// =============================================================================
// DESENHO DO CARD (no canvas lógico 300x468)
// =============================================================================
function drawCard(cfg: ForgeCardConfig): void {
  const ctx = ensureLogical();
  ctx.clearRect(0, 0, LW, LH);
  ctx.imageSmoothingEnabled = false;

  const FX = 16, FY = 30, FW = LW - 32, FH = LH - FY - 16;

  // ----- moldura externa -----
  px(ctx, FX - 3, FY - 3, FW + 6, FH + 6, OUT);
  bevelRect(ctx, FX, FY, FW, FH, GOLD, 3);
  px(ctx, FX + 4, FY + 4, FW - 8, 1, GOLD_HI);
  px(ctx, FX + 4, FY + 4, 1, FH - 8, GOLD_HI);
  px(ctx, FX + 4, FY + FH - 6, FW - 8, 2, GOLD_DK);
  px(ctx, FX + FW - 6, FY + 4, 2, FH - 8, GOLD_DK);
  px(ctx, FX + 7, FY + 7, FW - 14, FH - 14, OUT);
  px(ctx, FX + 9, FY + 9, FW - 18, FH - 18, DARK);

  // rebites (pulam a crista no topo e as gemas nos cantos)
  for (let x = FX + 40; x < FX + FW - 36; x += 30) {
    if (Math.abs(x - LW / 2) > 30) stud(ctx, x, FY + 5);
    stud(ctx, x, FY + FH - 5);
  }
  for (let y = FY + 46; y < FY + FH - 40; y += 34) { stud(ctx, FX + 5, y); stud(ctx, FX + FW - 5, y); }

  // ----- banner do título -----
  const th = cfg.theme;
  const bx = 44, by = 48, bw = LW - 88, bh = 40;
  function tail(dir: number): void {
    const w = 14, x0 = dir < 0 ? bx - w : bx + bw;
    px(ctx, x0 - 1, by + 9, w + 2, bh - 16, OUT);
    px(ctx, x0, by + 10, w, bh - 18, shade(th, -.55));
    px(ctx, x0, by + 10, w, 2, shade(th, -.25));
    px(ctx, x0, by + bh - 10, w, 2, shade(th, -.7));
    const nx = dir < 0 ? x0 - 1 : x0 + w + 1;
    tri(ctx, nx, by + 9, nx, by + bh - 7, nx + dir * -9, by + bh / 2 + 1, DARK);
    ctx.strokeStyle = OUT; ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(nx, by + 9); ctx.lineTo(nx + dir * -9, by + bh / 2 + 1); ctx.lineTo(nx, by + bh - 7);
    ctx.stroke();
  }
  tail(-1); tail(1);

  px(ctx, bx - 3, by - 3, bw + 6, bh + 6, OUT);
  px(ctx, bx - 1, by - 1, bw + 2, bh + 2, GOLD_DK);
  bevelRect(ctx, bx, by, bw, bh, GOLD, 2);
  px(ctx, bx + 2, by + 2, bw - 4, 1, GOLD_HI);
  px(ctx, bx + 4, by + 4, bw - 8, bh - 8, OUT);
  bevelRect(ctx, bx + 5, by + 5, bw - 10, bh - 10, th, 2);
  ctx.globalAlpha = .20;
  px(ctx, bx + 7, by + 7, bw - 14, (bh - 14) / 2, '#ffffff');
  ctx.globalAlpha = 1;
  stud(ctx, bx + 6, by + 6); stud(ctx, bx + bw - 6, by + 6);
  stud(ctx, bx + 6, by + bh - 6); stud(ctx, bx + bw - 6, by + bh - 6);

  let fs = 30;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  while (fs > 14) { ctx.font = fs + 'px VT323, monospace'; if (ctx.measureText(cfg.title).width <= bw - 26) break; fs--; }
  ctx.fillStyle = 'rgba(0,0,0,.85)';
  ctx.fillText(cfg.title, LW / 2 + 2, by + bh / 2 + 3);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(cfg.title, LW / 2, by + bh / 2 + 1);

  // ----- janela de arte ----- (mais baixa pra sobrar painel de texto grande)
  const ax = 28, ay = 100, aw = LW - 56, ah = 206;
  px(ctx, ax - 4, ay - 4, aw + 8, ah + 8, GOLD_LO);
  px(ctx, ax - 4, ay - 4, aw + 8, 2, GOLD_MID);
  px(ctx, ax - 4, ay - 4, 2, ah + 8, GOLD_MID);
  px(ctx, ax - 2, ay - 2, aw + 4, ah + 4, OUT);
  px(ctx, ax, ay, aw, ah, '#0b0b10');

  if (cfg.img) {
    const img = cfg.img;
    const zoom = cfg.imgZoom ?? 1, offX = cfg.imgOffX ?? 0, offY = cfg.imgOffY ?? 0;
    ctx.save();
    ctx.beginPath(); ctx.rect(ax, ay, aw, ah); ctx.clip();
    const cover = Math.max(aw / img.width, ah / img.height) * zoom;
    const dw = img.width * cover, dh = img.height * cover;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, ax + (aw - dw) / 2 + offX, ay + (ah - dh) / 2 + offY, dw, dh);
    const grad = ctx.createRadialGradient(LW / 2, ay + ah / 2, ah * .35, LW / 2, ay + ah / 2, ah * .9);
    grad.addColorStop(0, 'rgba(0,0,0,0)'); grad.addColorStop(1, 'rgba(0,0,0,.35)');
    ctx.fillStyle = grad; ctx.fillRect(ax, ay, aw, ah);
    ctx.restore();
  } else {
    for (let yy = 0; yy < ah; yy += 12) for (let xx = 0; xx < aw; xx += 12)
      px(ctx, ax + xx, ay + yy, 12, 12, ((xx + yy) / 12) % 2 ? '#14141c' : '#0d0d13');
    ctx.font = '20px VT323, monospace'; ctx.fillStyle = '#5a5a70';
    ctx.textAlign = 'center'; ctx.fillText('[ arte aqui ]', LW / 2, ay + ah / 2);
  }
  px(ctx, ax, ay, aw, 2, GOLD_MID); px(ctx, ax, ay + ah - 2, aw, 2, GOLD_LO);
  px(ctx, ax, ay, 2, ah, GOLD_MID); px(ctx, ax + aw - 2, ay, 2, ah, GOLD_LO);
  function bracket(x: number, y: number, dx: number, dy: number): void {
    px(ctx, x, y, dx * 14, dy * 3, GOLD_HI);
    px(ctx, x, y, dx * 3, dy * 14, GOLD_HI);
    px(ctx, x + dx * 2, y + dy * 2, dx * 10, dy * 1, GOLD_LO);
    px(ctx, x + dx * 2, y + dy * 2, dx * 1, dy * 10, GOLD_LO);
  }
  bracket(ax, ay, 1, 1); bracket(ax + aw, ay, -1, 1);
  bracket(ax, ay + ah, 1, -1); bracket(ax + aw, ay + ah, -1, -1);

  // ----- barra de mecânica -----
  const tx = 28, ty = 316, tw = LW - 56, thh = 28;
  px(ctx, tx - 3, ty - 3, tw + 6, thh + 6, OUT);
  px(ctx, tx - 1, ty - 1, tw + 2, thh + 2, GOLD_DK);
  bevelRect(ctx, tx, ty, tw, thh, '#262012', 2);
  px(ctx, tx, ty, tw, 2, GOLD_MID); px(ctx, tx, ty + thh - 2, tw, 2, GOLD_LO);
  px(ctx, tx, ty, 2, thh, GOLD_LO); px(ctx, tx + tw - 2, ty, 2, thh, GOLD_LO);
  gem(ctx, tx + 17, ty + thh / 2, 6, cfg.gemL, true);
  gem(ctx, tx + tw - 17, ty + thh / 2, 6, cfg.gemR, true);
  ctx.font = '21px VT323, monospace'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillStyle = 'rgba(0,0,0,.85)';
  ctx.fillText(cfg.tag, LW / 2 + 1, ty + thh / 2 + 3);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(cfg.tag, LW / 2, ty + thh / 2 + 2);

  // ----- painel de descrição ----- (grande: 2-3 linhas em corpo bem legível)
  const dx2 = 28, dy2 = 354, dw2 = LW - 56, dh2 = FY + FH - 9 - 6 - 354;
  px(ctx, dx2 - 3, dy2 - 3, dw2 + 6, dh2 + 6, OUT);
  bevelRect(ctx, dx2, dy2, dw2, dh2, PANEL, 2);
  px(ctx, dx2, dy2, dw2, 1, '#3a2f1c');
  const layout = layoutDesc(ctx, cfg.desc, dw2 - 20, dh2 - 8);
  ctx.font = layout.font; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  const lh = layout.lineHeight, y0 = dy2 + dh2 / 2 - ((layout.lines.length - 1) * lh) / 2;
  layout.lines.forEach((l, i) => {
    ctx.fillStyle = 'rgba(0,0,0,.85)'; ctx.fillText(l, LW / 2 + 1, y0 + i * lh + 2);
    ctx.fillStyle = '#efe6d2'; ctx.fillText(l, LW / 2, y0 + i * lh + 1);
  });

  // ----- gemas de canto -----
  gem(ctx, FX + 5, FY + 5, 9, cfg.gemCorner, true);
  gem(ctx, FX + FW - 5, FY + 5, 9, cfg.gemCorner, true);
  gem(ctx, FX + 5, FY + FH - 5, 9, cfg.gemCorner, true);
  gem(ctx, FX + FW - 5, FY + FH - 5, 9, cfg.gemCorner, true);

  // ----- crista do topo -----
  tri(ctx, LW / 2 - 14, FY + 4, LW / 2 - 36, FY + 4, LW / 2 - 14, FY - 7, GOLD_DK);
  tri(ctx, LW / 2 + 14, FY + 4, LW / 2 + 36, FY + 4, LW / 2 + 14, FY - 7, GOLD_DK);
  tri(ctx, LW / 2 - 15, FY + 3, LW / 2 - 32, FY + 3, LW / 2 - 15, FY - 5, GOLD);
  tri(ctx, LW / 2 + 15, FY + 3, LW / 2 + 32, FY + 3, LW / 2 + 15, FY - 5, GOLD);
  px(ctx, LW / 2 - 32, FY + 1, 64, 2, GOLD_HI);
  tri(ctx, LW / 2 - 20, FY + 5, LW / 2 + 20, FY + 5, LW / 2, FY - 14, GOLD_DK);
  px(ctx, LW / 2 - 16, FY - 4, 32, 10, GOLD);
  px(ctx, LW / 2 - 16, FY - 4, 32, 2, GOLD_HI);
  px(ctx, LW / 2 - 16, FY + 4, 32, 2, GOLD_LO);
  stud(ctx, LW / 2 - 11, FY + 1); stud(ctx, LW / 2 + 11, FY + 1);
  gem(ctx, LW / 2, 24, 13, cfg.gemTop, true);
  sparkle(ctx, LW / 2 - 26, 12, .8);
  sparkle(ctx, LW / 2 + 24, 18, .6);
}

// =============================================================================
// DESENHO DEITADO (no canvas lógico 470x232): arte à esquerda, textos à direita
// =============================================================================
function drawCardH(cfg: ForgeCardConfig): void {
  const ctx = ensureLogicalH();
  ctx.clearRect(0, 0, LWH, LHH);
  ctx.imageSmoothingEnabled = false;

  const FX = 14, FY = 22, FW = LWH - 28, FH = LHH - FY - 14;

  // ----- moldura externa -----
  px(ctx, FX - 3, FY - 3, FW + 6, FH + 6, OUT);
  bevelRect(ctx, FX, FY, FW, FH, GOLD, 3);
  px(ctx, FX + 4, FY + 4, FW - 8, 1, GOLD_HI);
  px(ctx, FX + 4, FY + 4, 1, FH - 8, GOLD_HI);
  px(ctx, FX + 4, FY + FH - 6, FW - 8, 2, GOLD_DK);
  px(ctx, FX + FW - 6, FY + 4, 2, FH - 8, GOLD_DK);
  px(ctx, FX + 7, FY + 7, FW - 14, FH - 14, OUT);
  px(ctx, FX + 9, FY + 9, FW - 18, FH - 18, DARK);
  for (let x = FX + 34; x < FX + FW - 30; x += 34) {
    if (Math.abs(x - LWH / 2) > 26) stud(ctx, x, FY + 5);
    stud(ctx, x, FY + FH - 5);
  }
  stud(ctx, FX + 5, FY + (FH >> 1)); stud(ctx, FX + FW - 5, FY + (FH >> 1));

  // ----- janela de arte (esquerda) -----
  const ax = 26, ay = 40, aw = 176, ah = 160;
  px(ctx, ax - 4, ay - 4, aw + 8, ah + 8, GOLD_LO);
  px(ctx, ax - 4, ay - 4, aw + 8, 2, GOLD_MID);
  px(ctx, ax - 4, ay - 4, 2, ah + 8, GOLD_MID);
  px(ctx, ax - 2, ay - 2, aw + 4, ah + 4, OUT);
  px(ctx, ax, ay, aw, ah, '#0b0b10');
  if (cfg.img) {
    const img = cfg.img;
    const zoom = cfg.imgZoom ?? 1, offX = cfg.imgOffX ?? 0, offY = cfg.imgOffY ?? 0;
    ctx.save();
    ctx.beginPath(); ctx.rect(ax, ay, aw, ah); ctx.clip();
    const cover = Math.max(aw / img.width, ah / img.height) * zoom;
    const dw = img.width * cover, dh = img.height * cover;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, ax + (aw - dw) / 2 + offX, ay + (ah - dh) / 2 + offY, dw, dh);
    const grad = ctx.createRadialGradient(ax + aw / 2, ay + ah / 2, ah * .35, ax + aw / 2, ay + ah / 2, ah * .9);
    grad.addColorStop(0, 'rgba(0,0,0,0)'); grad.addColorStop(1, 'rgba(0,0,0,.35)');
    ctx.fillStyle = grad; ctx.fillRect(ax, ay, aw, ah);
    ctx.restore();
  } else {
    for (let yy = 0; yy < ah; yy += 12) for (let xx = 0; xx < aw; xx += 12)
      px(ctx, ax + xx, ay + yy, 12, 12, ((xx + yy) / 12) % 2 ? '#14141c' : '#0d0d13');
  }
  px(ctx, ax, ay, aw, 2, GOLD_MID); px(ctx, ax, ay + ah - 2, aw, 2, GOLD_LO);
  px(ctx, ax, ay, 2, ah, GOLD_MID); px(ctx, ax + aw - 2, ay, 2, ah, GOLD_LO);
  const bracketH = (x: number, y: number, dx: number, dy: number): void => {
    px(ctx, x, y, dx * 12, dy * 3, GOLD_HI);
    px(ctx, x, y, dx * 3, dy * 12, GOLD_HI);
    px(ctx, x + dx * 2, y + dy * 2, dx * 9, dy * 1, GOLD_LO);
    px(ctx, x + dx * 2, y + dy * 2, dx * 1, dy * 9, GOLD_LO);
  };
  bracketH(ax, ay, 1, 1); bracketH(ax + aw, ay, -1, 1);
  bracketH(ax, ay + ah, 1, -1); bracketH(ax + aw, ay + ah, -1, -1);

  // ----- coluna direita: banner, mecânica, descrição -----
  const cx0 = ax + aw + 16, cw = FX + FW - 12 - cx0;
  const th = cfg.theme;

  // banner do título
  const by = 40, bh = 38;
  px(ctx, cx0 - 3, by - 3, cw + 6, bh + 6, OUT);
  px(ctx, cx0 - 1, by - 1, cw + 2, bh + 2, GOLD_DK);
  bevelRect(ctx, cx0, by, cw, bh, GOLD, 2);
  px(ctx, cx0 + 2, by + 2, cw - 4, 1, GOLD_HI);
  px(ctx, cx0 + 4, by + 4, cw - 8, bh - 8, OUT);
  bevelRect(ctx, cx0 + 5, by + 5, cw - 10, bh - 10, th, 2);
  ctx.globalAlpha = .20; px(ctx, cx0 + 7, by + 7, cw - 14, (bh - 14) / 2, '#ffffff'); ctx.globalAlpha = 1;
  stud(ctx, cx0 + 6, by + 6); stud(ctx, cx0 + cw - 6, by + 6);
  stud(ctx, cx0 + 6, by + bh - 6); stud(ctx, cx0 + cw - 6, by + bh - 6);
  let fs = 28;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  while (fs > 13) { ctx.font = fs + 'px VT323, monospace'; if (ctx.measureText(cfg.title).width <= cw - 22) break; fs--; }
  ctx.fillStyle = 'rgba(0,0,0,.85)'; ctx.fillText(cfg.title, cx0 + cw / 2 + 2, by + bh / 2 + 3);
  ctx.fillStyle = '#ffffff'; ctx.fillText(cfg.title, cx0 + cw / 2, by + bh / 2 + 1);

  // barra de mecânica
  const ty = 88, thh = 26;
  px(ctx, cx0 - 3, ty - 3, cw + 6, thh + 6, OUT);
  px(ctx, cx0 - 1, ty - 1, cw + 2, thh + 2, GOLD_DK);
  bevelRect(ctx, cx0, ty, cw, thh, '#262012', 2);
  px(ctx, cx0, ty, cw, 2, GOLD_MID); px(ctx, cx0, ty + thh - 2, cw, 2, GOLD_LO);
  px(ctx, cx0, ty, 2, thh, GOLD_LO); px(ctx, cx0 + cw - 2, ty, 2, thh, GOLD_LO);
  gem(ctx, cx0 + 15, ty + thh / 2, 6, cfg.gemL, true);
  gem(ctx, cx0 + cw - 15, ty + thh / 2, 6, cfg.gemR, true);
  ctx.font = '20px VT323, monospace';
  ctx.fillStyle = 'rgba(0,0,0,.85)'; ctx.fillText(cfg.tag, cx0 + cw / 2 + 1, ty + thh / 2 + 3);
  ctx.fillStyle = '#ffffff'; ctx.fillText(cfg.tag, cx0 + cw / 2, ty + thh / 2 + 2);

  // painel de descrição
  const dy = 122, dh = FY + FH - 14 - dy;
  px(ctx, cx0 - 3, dy - 3, cw + 6, dh + 6, OUT);
  bevelRect(ctx, cx0, dy, cw, dh, PANEL, 2);
  px(ctx, cx0, dy, cw, 1, '#3a2f1c');
  const layout = layoutDesc(ctx, cfg.desc, cw - 18, dh - 8);
  ctx.font = layout.font; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  const lh = layout.lineHeight, y0 = dy + dh / 2 - ((layout.lines.length - 1) * lh) / 2;
  layout.lines.forEach((l, i) => {
    ctx.fillStyle = 'rgba(0,0,0,.85)'; ctx.fillText(l, cx0 + cw / 2 + 1, y0 + i * lh + 2);
    ctx.fillStyle = '#efe6d2'; ctx.fillText(l, cx0 + cw / 2, y0 + i * lh + 1);
  });

  // ----- gemas de canto + crista central -----
  gem(ctx, FX + 5, FY + 5, 8, cfg.gemCorner, true);
  gem(ctx, FX + FW - 5, FY + 5, 8, cfg.gemCorner, true);
  gem(ctx, FX + 5, FY + FH - 5, 8, cfg.gemCorner, true);
  gem(ctx, FX + FW - 5, FY + FH - 5, 8, cfg.gemCorner, true);
  const mid = LWH / 2;
  tri(ctx, mid - 12, FY + 4, mid - 30, FY + 4, mid - 12, FY - 6, GOLD_DK);
  tri(ctx, mid + 12, FY + 4, mid + 30, FY + 4, mid + 12, FY - 6, GOLD_DK);
  px(ctx, mid - 28, FY + 1, 56, 2, GOLD_HI);
  px(ctx, mid - 14, FY - 4, 28, 9, GOLD);
  px(ctx, mid - 14, FY - 4, 28, 2, GOLD_HI);
  gem(ctx, mid, FY - 6, 11, cfg.gemTop, true);
  sparkle(ctx, mid - 24, FY - 14, .7);
}

// Proporção lógica do card (útil pra dimensionar o canvas de destino)
export const CARD_ASPECT = LW / LH;
export const CARD_LOGICAL = { w: LW, h: LH };
export const CARD_H_LOGICAL = { w: LWH, h: LHH };

// Desenha o card no canvas de destino (amplia com nearest-neighbor). `horizontal`
// usa a variante deitada (mobile). O canvas de destino deve estar dimensionado
// no múltiplo certo (ex.: 600x936 no retrato, 940x464 no deitado).
export function renderForgeCard(
  target: HTMLCanvasElement, cfg: ForgeCardConfig, horizontal = false,
): void {
  const src = horizontal ? (drawCardH(cfg), logicalH!) : (drawCard(cfg), logical!);
  const tctx = target.getContext('2d')!;
  tctx.imageSmoothingEnabled = false;
  tctx.clearRect(0, 0, target.width, target.height);
  tctx.drawImage(src, 0, 0, target.width, target.height);
}

// =============================================================================
// Cache/loader de imagens das cartas. A arte é opcional: enquanto não carrega
// (ou se o arquivo não existe), o card mostra o placeholder xadrez desenhado.
// =============================================================================
type ImgState = { img: HTMLImageElement | null; done: boolean };
const imgCache = new Map<string, ImgState>();

// Retorna a imagem se já carregou; senão dispara o carregamento e chama
// `onReady` quando terminar (pra re-renderizar aquele card). Nunca lança.
export function loadCardImage(src: string | undefined, onReady: () => void): HTMLImageElement | null {
  if (!src) return null;
  const cached = imgCache.get(src);
  if (cached) return cached.img;
  const state: ImgState = { img: null, done: false };
  imgCache.set(src, state);
  const img = new Image();
  img.onload = () => { state.img = img; state.done = true; onReady(); };
  img.onerror = () => { state.done = true; onReady(); };   // 404 → segue com placeholder
  img.src = src;
  return null;
}
